/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    getAvailableDateRange,
    type ItemWithAvailableDates,
} from './getAvailableDateRange';

/** Persisted search state that facility pages hydrate their dates from on load */
const SEARCH_SESSION_KEY = 'r1s_search_session';

/**
 * Formats a calendar date the way the search session stores dates
 * @param date the calendar date
 */
function toSearchSessionDate(date: { year: number; month: number; day: number }) {
    const month = String(date.month).padStart(2, '0');
    const day = String(date.day).padStart(2, '0');
    return `${month}/${day}/${date.year}`;
}

/**
 * Writes an item's first available date range into the search session in local
 * storage, so the facility page opened from the item starts on those dates.
 * ui-camping reads its dates from the session when it loads. The session's
 * `last_updated_datetime` is refreshed too, so it isn't expired as stale. Only
 * local storage changes: the current page's search state and URL are left
 * alone. Does nothing if the item has no available dates or there is no search
 * session.
 * @param item a search result or recommendation, which has `available_dates`
 * for flexible date searches
 */
export function saveAvailableDatesToSearchSession(item: ItemWithAvailableDates) {
    try {
        const dateRange = getAvailableDateRange(item);

        if (!dateRange) return;

        const searchSessionJson = window.localStorage.getItem(SEARCH_SESSION_KEY);

        if (!searchSessionJson) return;

        const searchSession = JSON.parse(searchSessionJson);
        searchSession.checkin_time = toSearchSessionDate(dateRange.start);
        searchSession.checkout_time = toSearchSessionDate(dateRange.end);
        // Retired legacy filters panel flag; a stale `true` hides ui-camping's
        // Modify Reservation grid
        delete searchSession.filtersVisible;
        // Facility pages drop sessions older than 4 hours, so mark this one fresh
        searchSession.last_updated_datetime = Date.now();
        window.localStorage.setItem(SEARCH_SESSION_KEY, JSON.stringify(searchSession));
    } catch {
        // Malformed session: the facility page just starts without dates
    }
}

// cSpell:ignore checkin_time
